import { tryParseSearchDateToMMDDYYYY } from '../tasks/utils';

/**
 * Field types for search matching:
 * - `text`: Simple case-insensitive text matching
 * - `name`: Handles "Lastname, Firstname" format - matches both original and reversed order
 * - `date`: Matches date strings and also parses search term as a date for format-agnostic matching
 */
export type SearchFieldType = 'text' | 'name' | 'date';

/**
 * Checks if a search term matches a field value based on the field type.
 */
export const doesSearchMatch = (
  searchTerm: string,
  value: string | null | undefined,
  fieldType: SearchFieldType,
): boolean => {
  if (!searchTerm || !value) return false;

  const term = searchTerm.toLowerCase();
  const valueLower = value.toLowerCase();

  switch (fieldType) {
    case 'text':
      return valueLower.includes(term);

    case 'name': {
      // Check direct match
      if (valueLower.includes(term)) return true;
      // Also check reversed name order (e.g., "Lastname, Firstname" -> "Firstname Lastname")
      if (valueLower.includes(', ')) {
        const reversed = valueLower.split(', ').reverse().join(' ');
        if (reversed.includes(term)) return true;
      }
      return false;
    }

    case 'date': {
      // Check direct string match
      if (valueLower.includes(term)) return true;
      // Also check if search term parses to the same date (handles different date formats)
      const parsedSearchDate = tryParseSearchDateToMMDDYYYY(searchTerm);
      if (parsedSearchDate && value === parsedSearchDate) return true;
      return false;
    }

    default:
      return false;
  }
};
